import { useCallback, useEffect, useRef } from 'react';
import usePopup, { Close, OnOpen, UsePopupConfig } from 'hooks/usePopup';
import useStaticCallback from 'hooks/useStaticCallback';
import { isOAuthMessage } from './utils';

type PickedPopupConfig = Pick<
  UsePopupConfig,
  'onClose' | 'onError' | 'onOpen' | 'url'
> &
  Partial<Pick<UsePopupConfig, 'target'>>;

export interface UseOAuthPopupConfig<TData> extends PickedPopupConfig {
  /**
   * Called when the user closes the popup to cancel the authentication flow
   */
  onCancel?: () => void;
  /**
   * Called on successful authentication with data passed from the backend
   */
  onSuccess?: (data: TData) => void;
  /**
   * The origin of the postMessage to be received by this hook.  If left blank,
   * this hook will accept postMessages from any origin, which could be dangerous.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
   */
  postMessageOrigin?: string;
}

/**
 * Adds authentication logic to the `usePopup` hook.
 *
 * This hook opens a popup and waits for the authentication result to be passed
 * to it via postMessage.  Once it receives the data, it closes the popup.
 *
 * If the popup is closed by the user before receving the auth result, `onClose`
 * is called without calling `onSuccess` or `onError`.
 */
export default function useOAuthPopup<TData>({
  onCancel,
  onClose,
  onError,
  onOpen,
  onSuccess,
  postMessageOrigin,
  url,
  target = '_blank',
}: UseOAuthPopupConfig<TData>) {
  const closeRef = useRef<Close>();
  const resolvedRef = useRef(false);

  const handlePostMessage = useStaticCallback((event: MessageEvent) => {
    const matchesOrigin =
      postMessageOrigin !== undefined
        ? event.origin === postMessageOrigin
        : true;

    if (matchesOrigin && isOAuthMessage<TData>(event)) {
      if (event.data.success) {
        onSuccess?.(event.data);
      } else {
        onError?.(new Error(event.data.errorMessage));
      }

      resolvedRef.current = true;
      closeRef.current?.();
    }
  });

  const handleOpen: OnOpen = useCallback(
    (popup, close) => {
      // store the close function so that it can be called outsief of this function
      closeRef.current = close;
      onOpen?.(popup, close);
      resolvedRef.current = false;
      window.addEventListener('message', handlePostMessage);
    },
    [handlePostMessage, onOpen],
  );

  const handleClose = useCallback(() => {
    if (!resolvedRef.current) {
      onCancel?.();
    }

    onClose?.();
    window.removeEventListener('message', handlePostMessage);
  }, [handlePostMessage, onCancel, onClose]);

  // safeguard to remove the postmessage listener when the component unmounts,
  // in case for some reason that happens before onClose is called
  useEffect(
    () => () => {
      window.removeEventListener('message', handlePostMessage);
    },
    [handlePostMessage],
  );

  return usePopup({
    onError,
    target,
    url,
    onClose: handleClose,
    onOpen: handleOpen,
  });
}
